import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { Eye, EyeOff, LockKeyhole, LogOut } from "lucide-react";
import { updatePassword } from "firebase/auth";
import { auth, db } from "./firebaseConfig";
import studyDenHorizontal from "./assets/images/branding/Studyden_Horizontal.svg";
import "./ChangePasswordRequired.css";

const ChangePasswordRequired = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");

  useEffect(() => {
    const isDarkMode = localStorage.getItem("darkMode") === "true";
    setDarkMode(isDarkMode);

    if (isDarkMode) {
      document.body.classList.add("dark-mode");
      document.documentElement.setAttribute("data-dark-mode", "true");
    } else {
      document.body.classList.remove("dark-mode");
      document.documentElement.setAttribute("data-dark-mode", "false");
    }
  }, []);

  const passwordChecks = useMemo(() => {
    return [
      {
        label: "At least 8 characters",
        valid: newPassword.length >= 8,
      },
      {
        label: "Includes a letter and a number",
        valid: /[A-Za-z]/.test(newPassword) && /\d/.test(newPassword),
      },
      {
        label: "Passwords match",
        valid: newPassword.length > 0 && newPassword === confirmPassword,
      },
    ];
  }, [newPassword, confirmPassword]);

  const isPasswordReady = passwordChecks.every((check) => check.valid);

  const getFirebaseErrorMessage = (error) => {
    switch (error.code) {
      case "auth/weak-password":
        return "That password is too weak. Choose a stronger password and try again.";
      case "auth/requires-recent-login":
        return "For security, Firebase needs a fresh sign-in before changing this password. Please log out, sign back in with the temporary password, and try again.";
      case "auth/network-request-failed":
        return "Network error. Check your connection and try again.";
      default:
        return error.message || "Could not update your password. Please try again.";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);

    if (!isPasswordReady) {
      setMessage({
        type: "error",
        text: "Please meet the password requirements before continuing.",
      });
      return;
    }

    if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
      setMessage({
        type: "error",
        text: "Your sign-in session could not be verified. Please log out and sign in again.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await updatePassword(auth.currentUser, newPassword);
      await updateDoc(doc(db, "users", user.uid), {
        mustChangePassword: false,
      });

      setUser((currentUser) => (
        currentUser
          ? { ...currentUser, mustChangePassword: false }
          : currentUser
      ));

      setMessage({
        type: "success",
        text: "Password updated. Taking you to your dashboard...",
      });

      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 800);
    } catch (error) {
      console.error("Error setting required student password:", error);
      setMessage({
        type: "error",
        text: getFirebaseErrorMessage(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/", { replace: true });
  };

  return (
    <main className={`password-required-page ${darkMode ? "password-required-dark" : ""}`}>
      <section className="password-required-shell">
        <div className="password-required-brand">
          <img src={studyDenHorizontal} alt="StudyDen" className="password-required-logo" />
        </div>

        <div className="password-required-card">
          <div className="password-required-icon">
            <LockKeyhole size={32} strokeWidth={1.8} />
          </div>

          <h1>Create your password</h1>
          <p className="password-required-intro">
            Your account requires a new password before you can continue. Choose a new password below.
          </p>

          <form onSubmit={handleSubmit} className="password-required-form">
            <label htmlFor="new-password">New password</label>
            <div className="password-required-input-row">
              <input
                id="new-password"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                disabled={isSubmitting}
                required
              />
              <button
                type="button"
                className="password-required-icon-button"
                onClick={() => setShowNewPassword((visible) => !visible)}
                aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                title={showNewPassword ? "Hide new password" : "Show new password"}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <label htmlFor="confirm-password">Confirm new password</label>
            <div className="password-required-input-row">
              <input
                id="confirm-password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                disabled={isSubmitting}
                required
              />
              <button
                type="button"
                className="password-required-icon-button"
                onClick={() => setShowConfirmPassword((visible) => !visible)}
                aria-label={showConfirmPassword ? "Hide confirmation password" : "Show confirmation password"}
                title={showConfirmPassword ? "Hide confirmation password" : "Show confirmation password"}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <ul className="password-required-checks" aria-label="Password requirements">
              {passwordChecks.map((check) => (
                <li key={check.label} className={check.valid ? "valid" : ""}>
                  <span aria-hidden="true">{check.valid ? "OK" : "-"}</span>
                  {check.label}
                </li>
              ))}
            </ul>

            {message && (
              <div className={`password-required-message ${message.type}`} role="alert">
                {message.text}
              </div>
            )}

            <button
              type="submit"
              className="password-required-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Setting password..." : "Set Password"}
            </button>
          </form>

          <button
            type="button"
            className="password-required-logout"
            onClick={handleLogout}
            disabled={isSubmitting}
          >
            <LogOut size={18} />
            Log out
          </button>
        </div>
      </section>
    </main>
  );
};

export default ChangePasswordRequired;
